import { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Badge,
  Input,
  Modal,
  Toggle,
  useToast,
  type Column,
} from '@nexora/design-system';
import { formatDate } from '../../../shared/utils/format';
import {
  useTaxRules,
  useCreateTaxRule,
  useUpdateTaxRule,
  useDeleteTaxRule,
  type TaxRule,
  type TaxRuleInput,
  type TaxCondition,
  type TaxRuleAction,
  type TaxConditionOperator,
} from './useTaxRules';

/* ── Constants ── */

/**
 * Os campos que o motor de tributo resolve — e SOMENTE eles.
 *
 * `TaxRuleEntity.resolveFieldValue` tem um mapa fechado de sete nomes. A lista
 * anterior oferecia `uf`, `regime` e `productType`, que não estão no mapa:
 * a condição resolvia `undefined` e a regra **nunca casava com documento
 * nenhum**, sem erro e sem aviso. Oferecer um campo que o motor ignora é pior
 * que não oferecer campo.
 */
const CONDITION_FIELDS = [
  { value: 'operationType', label: 'Tipo de operação' },
  { value: 'ufOrigem', label: 'UF de origem' },
  { value: 'ufDestino', label: 'UF de destino' },
  { value: 'emitenteRegime', label: 'Regime do emitente' },
  { value: 'ncm', label: 'NCM' },
  { value: 'ncmPrefix', label: 'Prefixo do NCM' },
  { value: 'municipioIbge', label: 'Município (IBGE)' },
];

const OPERATION_LABEL: Record<string, string> = {
  VENDA: 'Venda',
  COMPRA: 'Compra',
  DEVOLUCAO_VENDA: 'Devolução de venda',
  DEVOLUCAO_COMPRA: 'Devolução de compra',
  TRANSFERENCIA: 'Transferência',
  SERVICO: 'Serviço',
  OUTROS: 'Outros',
};

/** Valor de uma condição, já legível (a condição pode trazer lista). */
function conditionValue(rule: TaxRule, field: string): string | null {
  const c = rule.conditions.find((x) => x.field === field);
  if (!c) return null;
  const v = c.value as unknown;
  if (Array.isArray(v)) return v.join(', ');
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

/** "SP → RJ" da referência, das condições de UF. `—` quando a regra não as usa. */
function scenario(rule: TaxRule): string {
  const origem = conditionValue(rule, 'ufOrigem');
  const destino = conditionValue(rule, 'ufDestino');
  if (!origem && !destino) return '—';
  return `${origem ?? 'qualquer'} → ${destino ?? 'qualquer'}`;
}

/** Tributos que a regra aplica: as ações com `apply`. */
function appliedTaxes(rule: TaxRule): string[] {
  return rule.actions.filter((a) => a.apply).map((a) => a.taxType);
}

type RuleStatus = 'ativa' | 'inativa' | 'agendada' | 'expirada';

/**
 * Situação da regra.
 *
 * `enabled` sozinho não diz se a regra vale hoje: `isEffective` do domínio
 * checa a janela de vigência junto. A tela mostrava só o interruptor, e uma
 * regra desligada pelo fim da vigência aparecia como ativa.
 */
function ruleStatus(rule: TaxRule, agora: Date): RuleStatus {
  if (!rule.enabled) return 'inativa';
  const inicio = new Date(rule.effectiveSince);
  if (!Number.isNaN(inicio.getTime()) && agora < inicio) return 'agendada';
  if (rule.effectiveUntil) {
    const fim = new Date(rule.effectiveUntil);
    if (!Number.isNaN(fim.getTime()) && agora > fim) return 'expirada';
  }
  return 'ativa';
}

const STATUS_LABEL: Record<RuleStatus, string> = {
  ativa: 'Ativa',
  inativa: 'Inativa',
  agendada: 'Agendada',
  expirada: 'Expirada',
};

const STATUS_VARIANT: Record<RuleStatus, 'success' | 'neutral' | 'info' | 'warning'> = {
  ativa: 'success',
  inativa: 'neutral',
  agendada: 'info',
  expirada: 'warning',
};

const CONDITION_OPERATORS: { value: TaxConditionOperator; label: string }[] = [
  { value: 'EQ', label: 'Igual (EQ)' },
  { value: 'NEQ', label: 'Diferente (NEQ)' },
  { value: 'IN', label: 'Em (IN)' },
  { value: 'STARTS_WITH', label: 'Começa com' },
  { value: 'RANGE', label: 'Intervalo' },
  { value: 'OR', label: 'OU' },
  { value: 'AND', label: 'E' },
];

/* ── Helpers ── */

function conditionsSummary(conditions: TaxCondition[]): string {
  return conditions
    .slice(0, 2)
    .map((c) => `${c.field} ${c.operator} ${c.value}`)
    .join(', ') + (conditions.length > 2 ? ` +${conditions.length - 2}` : '');
}

function emptyAction(): TaxRuleAction {
  return { taxType: 'ICMS', cst: '', rateType: 'PERCENT', apply: true };
}

function emptyRuleInput(): TaxRuleInput {
  return {
    name: '',
    conditions: [{ field: 'uf', operator: 'EQ', value: '' }],
    actions: [emptyAction()],
    priority: 0,
    enabled: true,
    effectiveSince: new Date().toISOString().split('T')[0] ?? '',
  };
}

/** Resumo legivel das acoes: "ICMS CST 00", "IPI CST 50 +1". */

/* ── Component ── */

export function TaxRulesPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: rules, isLoading, error, refetch } = useTaxRules();
  const createMutation = useCreateTaxRule();
  const updateMutation = useUpdateTaxRule();
  const deleteMutation = useDeleteTaxRule();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TaxRuleInput>(emptyRuleInput());
  const [deleteTarget, setDeleteTarget] = useState<TaxRule | null>(null);
  const [busca, setBusca] = useState('');
  const [tributoFiltro, setTributoFiltro] = useState('ALL');
  const [regimeFiltro, setRegimeFiltro] = useState('ALL');
  const [ufOrigemFiltro, setUfOrigemFiltro] = useState('ALL');
  const [ufDestinoFiltro, setUfDestinoFiltro] = useState('ALL');

  /* Instante unico para a coluna de situacao: com `new Date()` por linha, duas
     linhas da mesma tabela poderiam ser avaliadas em momentos diferentes. */
  const agora = useMemo(() => new Date(), []);

  // Reset form when modal opens
  useEffect(() => {
    if (!showModal) {
      setEditingId(null);
      setForm(emptyRuleInput());
    }
  }, [showModal]);

  function openCreate(): void {
    setEditingId(null);
    setForm(emptyRuleInput());
    setShowModal(true);
  }

  function openEdit(rule: TaxRule): void {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      ...(rule.description ? { description: rule.description } : {}),
      conditions: rule.conditions,
      actions: rule.actions,
      priority: rule.priority,
      enabled: rule.enabled,
      effectiveSince: rule.effectiveSince.split('T')[0] ?? rule.effectiveSince,
      ...(rule.effectiveUntil ? { effectiveUntil: rule.effectiveUntil.split('T')[0] } : {}),
    });
    setShowModal(true);
  }

  function handleSave(): void {
    if (editingId) {
      updateMutation.mutate({ id: editingId, ...form }, {
        onSuccess: () => {
          addToast('Regra atualizada com sucesso', 'success');
          setShowModal(false);
        },
        onError: (error) => addToast(error.message, 'error'),
      });
    } else {
      createMutation.mutate(form, {
        onSuccess: () => {
          addToast('Regra criada com sucesso', 'success');
          setShowModal(false);
        },
        onError: (error) => addToast(error.message, 'error'),
      });
    }
  }

  function handleToggleActive(rule: TaxRule): void {
    updateMutation.mutate({
      id: rule.id,
      name: rule.name,
      ...(rule.description ? { description: rule.description } : {}),
      conditions: rule.conditions,
      actions: rule.actions,
      priority: rule.priority,
      effectiveSince: rule.effectiveSince,
      enabled: !rule.enabled,
    }, {
      onSuccess: () => addToast('Status alterado', 'success'),
      onError: (error) => addToast(error.message, 'error'),
    });
  }

  function handleDelete(rule: TaxRule): void {
    deleteMutation.mutate(rule.id, {
      onSuccess: () => {
        addToast('Regra excluida com sucesso', 'success');
        setDeleteTarget(null);
      },
      onError: (error) => addToast(error.message, 'error'),
    });
  }

  function updateCondition(index: number, field: Partial<TaxCondition>): void {
    setForm((prev) => {
      const conditions: TaxCondition[] = [...prev.conditions];
      conditions[index] = { ...conditions[index], ...field } as TaxCondition;
      return { ...prev, conditions };
    });
  }

  function addCondition(): void {
    setForm((prev) => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'uf', operator: 'EQ', value: '' }],
    }));
  }

  function removeCondition(index: number): void {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  }

  const columns: Column<TaxRule>[] = [
    {
      header: 'Nome da regra',
      accessor: 'name' as const,
      render: (v: string, row: TaxRule) => (
        <span className="cell-name">
          <strong>{v}</strong>
          <small title={conditionsSummary(row.conditions)}>
            {conditionsSummary(row.conditions)}
          </small>
        </span>
      ),
    },
    {
      header: 'Tributos',
      accessor: 'actions' as const,
      render: (_v: TaxRuleAction[], row: TaxRule) => {
        const tributos = appliedTaxes(row);
        if (tributos.length === 0) return <span className="cell-muted">—</span>;
        /* Com sete tributos a coluna empurrava a tabela para fora do cartao e
           a coluna de acoes ficava cortada. Tres cabem; o resto conta. */
        const visiveis = tributos.slice(0, 3);
        const resto = tributos.length - visiveis.length;
        return (
          <span className="tax-chips" title={tributos.join(', ')}>
            {visiveis.map((t) => (
              <span key={t} className="tax-chip">{t}</span>
            ))}
            {resto > 0 ? <span className="tax-chip tax-chip--more">+{resto}</span> : null}
          </span>
        );
      },
    },
    {
      header: 'Descrição',
      accessor: 'description' as const,
      render: (v: string | null, row: TaxRule) => {
        const op = conditionValue(row, 'operationType');
        return (
          <span className="cell-desc-rule">
            {/* `max-width` sozinho nao corta: sem `overflow`/`ellipsis` o texto
                transbordava por cima da coluna de cenario. */}
            <span className="cell-desc-rule__text" title={v ?? undefined}>
              {v ?? <span className="cell-muted">—</span>}
            </span>
            {op ? <small>{OPERATION_LABEL[op] ?? op}</small> : null}
          </span>
        );
      },
    },
    {
      header: 'Cenário (UF)',
      accessor: 'id' as const,
      render: (_v: string, row: TaxRule) => (
        <span className="cell-scenario">{scenario(row)}</span>
      ),
    },
    {
      header: 'Vigência',
      accessor: 'effectiveSince' as const,
      render: (_v: string, row: TaxRule) => (
        <span className="cell-period">
          {formatDate(row.effectiveSince)}
          {row.effectiveUntil ? ` – ${formatDate(row.effectiveUntil)}` : ' →'}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'enabled' as const,
      render: (_v: boolean, row: TaxRule) => {
        const st = ruleStatus(row, agora);
        return (
          <span className="status-cell">
            <Badge variant={STATUS_VARIANT[st]} size="sm">{STATUS_LABEL[st]}</Badge>
            <Toggle checked={row.enabled} onChange={() => handleToggleActive(row)} size="sm" />
          </span>
        );
      },
    },
    { header: 'Prio.', accessor: 'priority' as const },
    {
      header: 'Ações',
      accessor: 'id' as const,
      render: (_v: string, row: TaxRule) => (
        /* Icone em vez de rotulo: com dois botoes de texto a coluna saia da
           area visivel em 1600px e so aparecia rolando a tabela. */
        <div className="cell-actions">
          <button
            type="button"
            className="row-action"
            aria-label={`Editar ${row.name}`}
            title="Editar"
            onClick={() => openEdit(row)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">edit</span>
          </button>
          <button
            type="button"
            className="row-action row-action--danger"
            aria-label={`Excluir ${row.name}`}
            title="Excluir"
            onClick={() => setDeleteTarget(row)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">delete</span>
          </button>
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  /* `GET /fiscal/tax-rules` devolve TODAS as regras do tenant — sem paginacao
     e sem parametro de filtro. Por isso o filtro aqui e client-side sem
     ressalva: a lista esta inteira na tela, nao e uma pagina de um conjunto
     maior (que foi o defeito do Painel Financeiro, T51). */
  const ufsConhecidas = Array.from(
    new Set(
      (rules ?? []).flatMap((r) =>
        [conditionValue(r, 'ufOrigem'), conditionValue(r, 'ufDestino')].filter(
          (v): v is string => !!v,
        ),
      ),
    ),
  ).sort();

  const tributosConhecidos = Array.from(
    new Set((rules ?? []).flatMap((r) => appliedTaxes(r))),
  ).sort();

  const regimesConhecidos = Array.from(
    new Set(
      (rules ?? [])
        .map((r) => conditionValue(r, 'emitenteRegime'))
        .filter((v): v is string => !!v),
    ),
  ).sort();

  const regrasFiltradas = (rules ?? []).filter((r) => {
    const termo = busca.trim().toLowerCase();
    if (termo) {
      const alvo = `${r.name} ${r.description ?? ''} ${conditionsSummary(r.conditions)}`;
      if (!alvo.toLowerCase().includes(termo)) return false;
    }
    if (tributoFiltro !== 'ALL' && !appliedTaxes(r).includes(tributoFiltro)) return false;
    if (regimeFiltro !== 'ALL' && conditionValue(r, 'emitenteRegime') !== regimeFiltro) return false;
    if (ufOrigemFiltro !== 'ALL' && conditionValue(r, 'ufOrigem') !== ufOrigemFiltro) return false;
    if (ufDestinoFiltro !== 'ALL' && conditionValue(r, 'ufDestino') !== ufDestinoFiltro) return false;
    return true;
  });

  return (
    <div className="tax-rules-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Repositório Global de Regras Fiscais
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            A lógica fiscal centralizada que o motor aplica, com vigência e cenário de cada regra.
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button variant="primary" onClick={openCreate}>
          Nova Regra
        </Button>
        </div>
      </div>

      <Card padding="1rem" className="filter-card">
        <div className="rule-filters">
          <Input
            label=""
            placeholder="Buscar por nome, descrição ou condição…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <select
            className="rule-select"
            aria-label="Tipo de tributo"
            value={tributoFiltro}
            onChange={(e) => setTributoFiltro(e.target.value)}
          >
            <option value="ALL">Todos os tributos</option>
            {tributosConhecidos.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            className="rule-select"
            aria-label="UF de origem"
            value={ufOrigemFiltro}
            onChange={(e) => setUfOrigemFiltro(e.target.value)}
          >
            <option value="ALL">UF origem: todas</option>
            {ufsConhecidas.map((uf) => (
              <option key={uf} value={uf}>{uf}</option>
            ))}
          </select>
          <select
            className="rule-select"
            aria-label="UF de destino"
            value={ufDestinoFiltro}
            onChange={(e) => setUfDestinoFiltro(e.target.value)}
          >
            <option value="ALL">UF destino: todas</option>
            {ufsConhecidas.map((uf) => (
              <option key={uf} value={uf}>{uf}</option>
            ))}
          </select>
          <select
            className="rule-select"
            aria-label="Regime do emitente"
            value={regimeFiltro}
            onChange={(e) => setRegimeFiltro(e.target.value)}
          >
            <option value="ALL">Todos os regimes</option>
            {regimesConhecidos.map((rg) => (
              <option key={rg} value={rg}>{rg}</option>
            ))}
          </select>
          <span className="rule-count">
            {regrasFiltradas.length} de {(rules ?? []).length}
          </span>
        </div>
      </Card>

      <Card>
        {error ? (
          <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem', color: '#ba1a1a' }}>
              Erro ao carregar regras
            </p>
            <p style={{ fontSize: '0.875rem', margin: '0 0 1rem', color: '#74777f' }}>
              {(error as Error).message}
            </p>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <Table
            columns={columns}
            data={regrasFiltradas}
            isLoading={isLoading}
            emptyMessage="Nenhuma regra tributária encontrada"
          />
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Regra Tributária' : 'Nova Regra Tributária'}
        size="lg"
      >
        <div className="modal-form">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex.: ICMS SP para RJ"
          />
          <Input
            label="Descrição"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descrição opcional"
          />

          {/* Conditions Section */}
          <div className="section-title">Condições</div>
          {form.conditions.map((cond, idx) => (
            <div key={idx} className="condition-row">
              <select
                className="form-select"
                value={cond.field}
                onChange={(e) => updateCondition(idx, { field: e.target.value })}
              >
                {CONDITION_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <select
                className="form-select"
                value={cond.operator}
                onChange={(e) => updateCondition(idx, { operator: e.target.value as TaxConditionOperator })}
              >
                {CONDITION_OPERATORS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <Input
                label=""
                value={cond.value}
                onChange={(e) => updateCondition(idx, { value: e.target.value })}
                placeholder="Valor"
              />
              {form.conditions.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeCondition(idx)}>
                  &times;
                </Button>
              )}
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={addCondition}>
            + Adicionar condição
          </Button>

          {/* Acao fiscal: o motor trabalha com uma LISTA de acoes, cada uma
              sobre um tributo. O formulario anterior tinha um par
              (tipo, valor) que nao existia no backend. */}
          <div className="section-title">Ações fiscais</div>
          {form.actions.map((action, idx) => (
            <div className="action-row" key={idx}>
              <select
                className="form-select"
                value={action.taxType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    actions: form.actions.map((a, i) =>
                      i === idx ? { ...a, taxType: e.target.value } : a,
                    ),
                  })
                }
              >
                {['ICMS', 'IPI', 'PIS', 'COFINS', 'ISS', 'IBS', 'CBS'].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <Input
                label="CST"
                value={action.cst}
                onChange={(e) =>
                  setForm({
                    ...form,
                    actions: form.actions.map((a, i) =>
                      i === idx ? { ...a, cst: e.target.value } : a,
                    ),
                  })
                }
                placeholder="Ex.: 00"
              />
              <Input
                label="Redução de base (%)"
                type="number"
                step="0.01"
                value={action.baseReduction === undefined ? '' : String(action.baseReduction)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    actions: form.actions.map((a, i) =>
                      i === idx
                        ? {
                            ...a,
                            ...(e.target.value
                              ? { baseReduction: parseFloat(e.target.value) }
                              : { baseReduction: undefined }),
                          }
                        : a,
                    ),
                  })
                }
              />
              {form.actions.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() =>
                    setForm({ ...form, actions: form.actions.filter((_, i) => i !== idx) })
                  }
                >
                  Remover
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => setForm({ ...form, actions: [...form.actions, emptyAction()] })}
          >
            + Adicionar ação
          </Button>

          {/* Vigencia: obrigatoria no contrato e ausente na tela anterior. */}
          <div className="action-row">
            <Input
              label="Vigência a partir de"
              type="date"
              value={form.effectiveSince}
              onChange={(e) => setForm({ ...form, effectiveSince: e.target.value })}
            />
            <Input
              label="Vigência até (opcional)"
              type="date"
              value={form.effectiveUntil ?? ''}
              onChange={(e) => setForm({ ...form, effectiveUntil: e.target.value || null })}
            />
          </div>

          <Input
            label="Prioridade"
            type="number"
            value={String(form.priority)}
            onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
          />

          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!form.name || isSaving}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>

          {(createMutation.isError || updateMutation.isError) && (
            <p className="error-message">
              {((createMutation.error ?? updateMutation.error) as Error).message}
            </p>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Confirmar Exclusão"
      >
        <p>
          Tem certeza que deseja excluir a regra <strong>{deleteTarget?.name}</strong>?
          Esta ação não pode ser desfeita.
        </p>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteTarget && handleDelete(deleteTarget)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
        {deleteMutation.isError && (
          <p className="error-message">{(deleteMutation.error as Error).message}</p>
        )}
      </Modal>

      <style>{`
        .filter-card { margin-bottom: 1rem; }
        .rule-filters { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
        .rule-filters > *:first-child { flex: 1; min-width: 220px; }
        .rule-select { height: 40px; padding: 0 0.625rem; border: 1px solid #c4c6cf; border-radius: 2px; background: #ffffff; font-size: 0.875rem; color: #191c1e; }
        .rule-count { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; white-space: nowrap; }

        .cell-name { display: flex; flex-direction: column; line-height: 1.25; max-width: 30ch; overflow: hidden; }
        .cell-name strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cell-name strong { font-size: 0.875rem; color: #191c1e; }
        .cell-name small { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; color: #74777f; max-width: 22ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cell-scenario { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .cell-period { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; white-space: nowrap; color: #43474e; }
        .cell-muted { color: #74777f; }

        .tax-chips { display: inline-flex; gap: 0.25rem; flex-wrap: wrap; max-width: 15ch; }
        .tax-chip--more { background: #e0e3e5; color: #43474e; }
        .tax-rules-page table { min-width: 100%; }
        .tax-rules-page .table-scroll { overflow-x: auto; }
        .tax-chip { padding: 1px 6px; background: #d6e3ff; color: #001b3c; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.02em; white-space: nowrap; }

        .cell-desc-rule { display: flex; flex-direction: column; line-height: 1.25; max-width: 24ch; overflow: hidden; }
        .cell-desc-rule__text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cell-desc-rule small { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; color: #74777f; }
        .cell-actions { display: inline-flex; gap: 0.25rem; }
        .row-action { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; background: none; border: 1px solid transparent; border-radius: 2px; color: #43474e; cursor: pointer; }
        .row-action:hover { background: #f2f4f6; border-color: #c4c6cf; }
        .row-action--danger:hover { background: #ffdad6; border-color: #ffb4ab; color: #ba1a1a; }
        .row-action .material-symbols-outlined { font-size: 17px; }
        .status-cell { display: inline-flex; align-items: center; gap: 0.5rem; }

        .tax-rules-page { padding: 1.5rem; }
        .cell-summary { font-size: 0.8125rem; color: #43474e; }
        .cell-actions { display: flex; gap: 0.25rem; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .modal-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.5rem; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .section-title { font-size: 1.25rem; line-height: 1.75rem; font-weight: 600; color: #191c1e; margin-top: 0.5rem; padding-bottom: 0.25rem; border-bottom: 1px solid #e0e3e5; }
        .condition-row { display: flex; gap: 0.5rem; align-items: flex-end; }
        .condition-row .form-select { padding: 0.5rem; border: 1px solid #c4c6cf; border-radius: 4px; font-size: 0.875rem; height: 40px; flex: 1; }
        .condition-row > .ds-input-wrapper { flex: 1; }
        .action-row { display: flex; gap: 0.5rem; align-items: flex-end; }
        .action-row .form-select { padding: 0.5rem; border: 1px solid #c4c6cf; border-radius: 4px; font-size: 0.875rem; height: 40px; flex: 1; }
        .action-row > .ds-input-wrapper { flex: 1; }
      `}</style>
    </div>
  );
}
